import React, { useState } from 'react';
import { AlertCircle, Book, BookOpen, ChevronDown, Mic, MicOff, Search, X } from 'lucide-react';
import BiblePicker from './BiblePicker';

const MODE_KEY = 'sleekHearFindMode';
const readMode = () => {
  try { return localStorage.getItem(MODE_KEY) === 'browse' ? 'browse' : 'type'; } catch { return 'type'; }
};

/**
 * Bible view: find a verse (type it, or browse books), listen to the preacher,
 * and the saved / recent passages. Each job is its own calm card.
 */
const BibleView = ({
  activeView,
  addDebugLog,
  addToVerseLibrary,
  audioDevices,
  audioLevel,
  clearRecentPassages,
  clearVerseLibrary,
  debugLog,
  detectedPassages,
  displayPassageByReference,
  dm,
  handleLibraryInputChange,
  handleSearch,
  handleSearchInputChange,
  intro,
  interimText,
  isListening,
  isRecognizing,
  librarySuggestions,
  micPermission,
  newVerseRef,
  recentPassages,
  removeFromVerseLibrary,
  searchQuery,
  searchSuggestions,
  selectedDeviceId,
  setLibrarySuggestions,
  setNewVerseRef,
  setRecentPassages,
  setSearchQuery,
  setSelectedDeviceId,
  setShowAddVerseForm,
  setTestMode,
  settings,
  showAddVerseForm,
  t,
  testMicrophone,
  testMode,
  toggleListening,
  transcribedText,
  updateSettings,
  user,
  verseLibrary,
}) => {
  const [findMode, setFindMode] = useState(readMode); // 'type' | 'browse'
  const [savedTab, setSavedTab] = useState('saved');  // 'saved' | 'recent'
  const [showVoiceOptions, setShowVoiceOptions] = useState(false);

  const chooseMode = (mode) => {
    setFindMode(mode);
    try { localStorage.setItem(MODE_KEY, mode); } catch { /* the choice just is not remembered */ }
  };

  const card = `p-4 rounded-xl border ${t.innerCardBg}`;
  const heading = `text-sm font-semibold flex items-center gap-1.5 ${dm ? 'text-blue-300' : 'text-blue-600'}`;
  const tab = (active) => `flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${active
    ? 'bg-brand-600 text-slate-50 shadow'
    : (dm ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}`;
  const quiet = `px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${dm ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`;
  const dangerQuiet = 'px-3 py-1.5 border border-red-500/40 bg-red-500/10 text-red-500 rounded-lg text-xs font-semibold hover:bg-red-600 hover:text-white transition-all';
  const row = `flex items-center rounded-lg overflow-hidden border transition-all ${t.rowBg}`;
  const removeBtn = 'px-3 py-2.5 text-slate-400 hover:bg-red-600 hover:text-white transition-all';

  return (
    <div className={`${activeView === 'bible' ? '' : 'hidden'} lg:col-span-2 space-y-4`}>
      <div className={`rounded-2xl p-5 border shadow-lg ${dm ? 'bg-gradient-to-br from-blue-900/40 to-slate-900/60 border-blue-500/30' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-lg font-bold flex items-center gap-2 ${dm ? 'text-blue-300' : 'text-blue-700'}`}>
            <div className="p-1.5 bg-brand-600 rounded-lg">
              <Book className="w-4 h-4 text-slate-50" />
            </div>
            Bible
          </h2>
        </div>

        {intro}

        <div className="space-y-4">
          {/* ---- Find a verse ---- */}
          <div className={card}>
            <h3 className={`${heading} mb-3`}>
              <Search className="w-4 h-4" />
              Find a verse
            </h3>
            <div className="flex gap-2 mb-3" role="tablist" aria-label="How to find a verse">
              <button type="button" role="tab" aria-selected={findMode === 'type'} className={tab(findMode === 'type')} onClick={() => chooseMode('type')}>
                Type a reference
              </button>
              <button type="button" role="tab" aria-selected={findMode === 'browse'} className={tab(findMode === 'browse')} onClick={() => chooseMode('browse')}>
                Browse books
              </button>
            </div>

            {findMode === 'type' ? (
              <>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchInputChange(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch(searchQuery)}
                    placeholder="Search e.g. John 3, John 3:16, or Genesis"
                    className={`w-full p-3 pr-9 text-sm rounded-lg border focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 outline-none transition-all ${t.inputBg}`}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                      className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 transition-colors ${dm ? 'text-slate-400 hover:text-slate-50' : 'text-gray-400 hover:text-gray-700'}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {searchSuggestions.length > 0 && (
                  <div className={`mt-2 rounded-lg border max-h-64 overflow-y-auto shadow-lg ${t.dropdownBg}`}>
                    {searchSuggestions.map((sugg, idx) => {
                      // 'book' keeps the user typing; everything else is a
                      // complete reference we can open immediately.
                      const isSearchable = sugg.type !== 'book';
                      return (
                        <button
                          key={idx}
                          onClick={() => {
                            if (isSearchable) {
                              handleSearch(sugg.display);
                            } else {
                              setSearchQuery(sugg.display);
                            }
                          }}
                          className={`w-full px-3 py-2.5 text-left text-sm border-b last:border-b-0 flex items-center justify-between gap-2 transition-all ${t.dropdownItem} ${t.dropdownDiv}`}
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            {sugg.type === 'book'
                              ? <BookOpen className="w-4 h-4 shrink-0 opacity-60" />
                              : <Search className="w-4 h-4 shrink-0 opacity-60" />}
                            <span className="truncate">{sugg.display}</span>
                          </span>
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${
                            sugg.type === 'book' ? (dm ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-gray-600')
                              : sugg.type === 'chapter' ? 'bg-amber-500/15 text-amber-500'
                              : 'bg-green-500/15 text-green-500'
                          }`}>
                            {sugg.type === 'book' ? 'Book' : sugg.type === 'chapter' ? 'Chapter' : 'Open'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {!searchQuery && (
                  <p className={`text-xs mt-2 ${t.textMuted}`}>Tip: type “jn 3 16” or “1 cor 13” and press Enter. Prefer clicking? Use Browse books.</p>
                )}
              </>
            ) : (
              <BiblePicker onSelect={handleSearch} dm={dm} t={t} />
            )}
          </div>

          {/* ---- Voice ---- */}
          <div className={card}>
            <div className="flex items-center justify-between mb-3 gap-2">
              <h3 className={heading}>
                <Mic className="w-4 h-4" />
                Voice
              </h3>
              <button
                type="button"
                onClick={() => setShowVoiceOptions(o => !o)}
                aria-expanded={showVoiceOptions}
                className={`${quiet} flex items-center gap-1`}
              >
                Options <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showVoiceOptions ? 'rotate-180' : ''}`} />
              </button>
            </div>

            <div className="flex gap-2">
              <button
                onClick={toggleListening}
                className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg font-bold text-sm shadow-lg transition-all ${isListening ? 'bg-gradient-to-r from-red-600 to-red-700 text-slate-50 animate-pulse' : 'bg-gradient-to-r from-green-600 to-emerald-600 text-slate-50 hover:from-green-500 hover:to-emerald-500'}`}
              >
                {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                {isListening ? 'Stop' : 'Start'}
              </button>
              <button
                onClick={testMicrophone}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-slate-50 rounded-lg hover:from-blue-500 hover:to-blue-600 font-semibold text-sm shadow-lg transition-all"
              >
                Test
              </button>
            </div>

            {showVoiceOptions && (
              <div className={`mt-3 pt-3 border-t space-y-3 ${t.divider || ''}`}>
                {audioDevices.length > 0 && (
                  <div>
                    <label htmlFor="audio-input" className={`block text-xs font-bold mb-1 ${t.textPrimary}`}>Audio Input</label>
                    <select
                      id="audio-input"
                      value={selectedDeviceId}
                      onChange={(e) => {
                        setSelectedDeviceId(e.target.value);
                        addDebugLog(`🎙️ Changed audio source: ${e.target.options[e.target.selectedIndex].text}`);
                      }}
                      disabled={isListening}
                      className={`w-full p-2.5 text-sm rounded-lg border focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 outline-none ${t.selectBg}`}
                    >
                      <option value="default">Default</option>
                      {audioDevices.map((device, idx) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || `Mic ${idx + 1}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <button onClick={() => setTestMode(!testMode)} className={quiet}>
                  {testMode ? 'Hide' : 'Debug'}
                </button>
              </div>
            )}

            {isListening && (
              <>
                <div className={`mt-3 p-2.5 rounded-lg border flex items-center gap-2 ${isRecognizing ? 'bg-green-900/30 border-green-400' : 'bg-yellow-900/30 border-yellow-400'}`}>
                  <div className={`w-2.5 h-2.5 rounded-full ${isRecognizing ? 'bg-green-400 animate-pulse' : 'bg-yellow-400'}`}></div>
                  <span className={`text-sm font-bold ${isRecognizing ? 'text-emerald-400' : 'text-yellow-200'}`}>
                    {isRecognizing ? 'Listening' : 'Waiting'}
                  </span>
                  <span className={`ml-auto text-xs font-mono font-bold px-1.5 py-0.5 rounded ${audioLevel > 20 ? 'text-green-400 bg-green-500/20' : 'text-red-400 bg-red-500/20'}`}>
                    Level {Math.round(audioLevel)}%
                  </span>
                </div>
                <div className="mt-2 w-full h-2.5 bg-gray-800 rounded-full overflow-hidden border border-slate-700">
                  <div
                    className="h-full transition-all duration-100 rounded-full"
                    style={{
                      width: `${audioLevel}%`,
                      background: audioLevel > 50 ? 'linear-gradient(to right, #22c55e, #10b981)' : audioLevel > 20 ? 'linear-gradient(to right, #eab308, #f59e0b)' : 'linear-gradient(to right, #ef4444, #dc2626)'
                    }}
                  ></div>
                </div>
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="sensitivity" className={`text-xs font-bold ${t.textPrimary}`}>Sensitivity</label>
                    <span className="text-xs text-blue-400 font-bold">{settings.audioGain}x</span>
                  </div>
                  <input
                    id="sensitivity"
                    type="range"
                    min="1"
                    max="20"
                    value={settings.audioGain}
                    onChange={(e) => updateSettings({ ...settings, audioGain: parseInt(e.target.value) })}
                    className="w-full h-2 bg-blue-950 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                  />
                </div>
              </>
            )}

            {testMode && debugLog.length > 0 && (
              <div className={`mt-3 rounded-lg p-2 border ${dm ? 'bg-gray-900/90 border-slate-700' : 'bg-gray-100 border-gray-300'}`}>
                {debugLog.slice(-3).map((log, idx) => (
                  <p key={idx} className="text-xs text-green-500 font-mono">{log}</p>
                ))}
              </div>
            )}

            {micPermission === 'denied' && (
              <div className={`mt-3 p-2.5 rounded-lg border flex items-center gap-2 ${dm ? 'bg-red-900/30 border-red-500/50' : 'bg-red-50 border-red-300'}`}>
                <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                <p className="text-xs text-red-500 font-semibold">
                  Microphone blocked — allow access in your browser's site settings, then reload.
                </p>
              </div>
            )}

            {(transcribedText || interimText) && (
              <div className={`mt-3 p-2.5 rounded-lg border ${dm ? 'bg-green-900/30 border-green-400' : 'bg-green-50 border-green-400'}`}>
                <p className="text-sm text-green-700 font-semibold">
                  {transcribedText}
                  {interimText && <span className="italic opacity-60"> {interimText}</span>}
                </p>
              </div>
            )}

            {detectedPassages.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {detectedPassages.map((p, idx) => (
                  <button
                    key={idx}
                    onClick={() => displayPassageByReference(p.reference)}
                    className="px-3 py-1.5 bg-gradient-to-r from-yellow-600 to-amber-600 text-slate-50 rounded-lg text-sm font-semibold hover:from-yellow-500 hover:to-amber-500"
                  >
                    {p.reference}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ---- Saved and recent ---- */}
          <div className={card}>
            <div className="flex gap-2 mb-3" role="tablist" aria-label="Saved or recent verses">
              <button type="button" role="tab" aria-selected={savedTab === 'saved'} className={tab(savedTab === 'saved')} onClick={() => setSavedTab('saved')}>
                Saved verses{verseLibrary.length > 0 ? ` (${verseLibrary.length})` : ''}
              </button>
              <button type="button" role="tab" aria-selected={savedTab === 'recent'} className={tab(savedTab === 'recent')} onClick={() => setSavedTab('recent')}>
                Recent{recentPassages.length > 0 ? ` (${recentPassages.length})` : ''}
              </button>
            </div>

            {savedTab === 'saved' ? (
              <>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className={`text-xs ${t.textSecondary}`}>Click a verse to stage it.</p>
                  <div className="flex gap-1.5">
                    {verseLibrary.length > 0 && (
                      <button onClick={clearVerseLibrary} className={dangerQuiet}>Clear</button>
                    )}
                    <button
                      onClick={() => setShowAddVerseForm(!showAddVerseForm)}
                      className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-slate-50 rounded-lg text-xs font-semibold hover:from-blue-500 hover:to-blue-600 shadow-lg transition-all"
                    >
                      {showAddVerseForm ? 'X' : '+'}
                    </button>
                  </div>
                </div>

                {showAddVerseForm && (
                  <div className={`mb-3 p-3 rounded-lg border ${t.innerCardBg}`}>
                    <div className="relative">
                      <input
                        type="text"
                        value={newVerseRef}
                        onChange={(e) => handleLibraryInputChange(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addToVerseLibrary()}
                        placeholder="John 3:16"
                        className={`w-full p-3 text-sm rounded-lg border focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 outline-none mb-2 ${t.inputBg}`}
                      />
                      {newVerseRef && (
                        <button
                          onClick={() => setNewVerseRef('')}
                          aria-label="Clear"
                          className={`absolute right-2 top-6 -translate-y-1/2 transition-colors ${dm ? 'text-slate-400 hover:text-slate-50' : 'text-gray-400 hover:text-gray-700'}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {librarySuggestions.length > 0 && (
                      <div className={`mb-2 rounded-lg border max-h-32 overflow-y-auto shadow-lg ${t.dropdownBg}`}>
                        {librarySuggestions.map((sugg, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              if (sugg.type === 'reference' || sugg.type === 'verse') {
                                setNewVerseRef(sugg.display);
                                setLibrarySuggestions([]);
                              } else {
                                setNewVerseRef(sugg.value);
                              }
                            }}
                            className={`w-full p-2 text-left text-sm border-b last:border-b-0 flex items-center justify-between transition-all ${t.dropdownItem} ${t.dropdownDiv}`}
                          >
                            <span>{sugg.display}</span>
                            {sugg.type === 'reference' && <span className="text-green-500 text-xs font-semibold bg-green-500/10 px-1.5 py-0.5 rounded-full">✓</span>}
                          </button>
                        ))}
                      </div>
                    )}

                    <button
                      onClick={addToVerseLibrary}
                      className="w-full px-3 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-slate-50 rounded-lg text-sm hover:from-green-500 hover:to-emerald-500 font-bold shadow-lg transition-all"
                    >
                      Add
                    </button>
                  </div>
                )}

                {verseLibrary.length === 0 && !showAddVerseForm ? (
                  <p className={`text-sm py-4 text-center ${t.textSecondary}`}>
                    No saved verses yet. Press + to save the ones you use every week.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {verseLibrary.map((verse, idx) => (
                      <div key={idx} className={`${row} hover:border-blue-400/50`}>
                        <button
                          onClick={() => displayPassageByReference(verse.reference)}
                          className={`flex-1 p-3 hover:bg-blue-500/10 text-left transition-all ${t.textPrimary}`}
                        >
                          <div className="text-sm font-semibold">{verse.reference}</div>
                        </button>
                        <button
                          onClick={() => removeFromVerseLibrary(idx)}
                          title="Remove" aria-label="Remove"
                          className={removeBtn}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {recentPassages.length > 0 && (
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className={`text-xs ${t.textSecondary}`}>Passages you opened this session.</p>
                    <button onClick={clearRecentPassages} className={dangerQuiet}>Clear</button>
                  </div>
                )}
                {recentPassages.length === 0 ? (
                  <p className={`text-sm py-4 text-center ${t.textSecondary}`}>Nothing yet. Passages you open appear here.</p>
                ) : (
                  <div className="space-y-1.5">
                    {recentPassages.map((ref, idx) => (
                      <div key={idx} className={`${row} hover:border-amber-400/50`}>
                        <button
                          onClick={() => displayPassageByReference(ref)}
                          className="flex-1 p-3 hover:bg-amber-500/10 text-left transition-all"
                        >
                          <div className={`text-sm font-semibold ${t.textPrimary}`}>{ref}</div>
                        </button>
                        <button
                          onClick={() => setRecentPassages(prev => prev.filter((_, i) => i !== idx))}
                          title="Remove" aria-label="Remove"
                          className={removeBtn}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BibleView;
